import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, Loader2, Check, X, Archive, Download } from 'lucide-react';
import api, { ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/auth/hooks/useAuth';
import { cn } from '@/lib/utils';
import { HUMAN_REVIEW_REQUIRED_PERMISSIONS } from '@/lib/human-review-permissions';

interface FindingRow {
  severity?: string;
  category: string;
  description?: string;
  file?: string;
  line?: number;
  evidence?: string;
  suggestion?: string;
  package?: string;
  version?: string;
}

const SEVERITY_BG: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  low: 'bg-lime-100 text-lime-800 dark:bg-lime-900/40 dark:text-lime-300',
  info: 'bg-zinc-100 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-300',
};

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  pending: {
    label: 'Pending',
    className: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  },
  reviewing: {
    label: 'AI reviewing',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  human_review: {
    label: 'Awaiting human reviewer',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  building: {
    label: 'Building',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  published: {
    label: 'Published',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  },
  rejected: {
    label: 'Rejected',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  },
};

function StatusBadge({ status }: { status: string }) {
  const style = STATUS_STYLES[status] ?? {
    label: status,
    className: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center px-3 py-1 rounded text-sm font-medium',
        style.className,
      )}
    >
      {style.label}
    </span>
  );
}

function FindingsList({ title, findings }: { title: string; findings: FindingRow[] }) {
  if (findings.length === 0) return null;
  return (
    <Card className="p-4">
      <h3 className="font-semibold mb-3">
        {title} <span className="text-muted-foreground font-normal">({findings.length})</span>
      </h3>
      <ul className="space-y-3">
        {findings.map((f, i) => {
          const sev = f.severity ?? 'info';
          const where = f.file
            ? `${f.file}${f.line ? `:${f.line}` : ''}`
            : f.package
              ? `${f.package}${f.version ? `@${f.version}` : ''}`
              : f.category;
          return (
            <li key={i} className="text-sm">
              <div className="flex items-start gap-2 flex-wrap">
                <span
                  className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase',
                    SEVERITY_BG[sev] ?? SEVERITY_BG.info,
                  )}
                >
                  {sev}
                </span>
                <span className="font-mono text-xs text-foreground">{where}</span>
              </div>
              {f.description && <p className="mt-1 ml-1 text-foreground">{f.description}</p>}
              {f.suggestion && <p className="mt-1 ml-1 text-muted-foreground">→ {f.suggestion}</p>}
              {f.evidence && !f.file && (
                <code className="mt-1 ml-1 inline-block text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                  {f.evidence}
                </code>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/**
 * Copy for each reason the server can record, keyed by its stored value.
 *
 * `risk_band` is deliberately absent: its copy depends on the score and the
 * findings count, so it is built below rather than looked up.
 */
const ESCALATION_REASON_COPY = {
  incomplete_review:
    'Part of this submission was never read by our automated review, so some of it went unchecked. That always gets a human review before publishing — the risk score only reflects the files we could read.',
  incomplete_content_review:
    'Part of the content pack in this submission could not be checked by our automated review or image safety screen. That always gets a human review before publishing — every publish reviews all of the content again.',
  incomplete_synthesis:
    'Every file in this submission was read and cleared individually, but our final check on how the parts fit together did not complete. That always gets a human review before publishing — it is not a finding against your code.',
  encrypted_payload:
    'This submission encrypts or bundles content that our automated review cannot read, so it always gets a human review before publishing, whatever its risk score. A reviewer checks the first version and every update.',
  sensitive_permission:
    'Plugins that request access to user documents or signature requests always get a human review before publishing, whatever their risk score. A reviewer checks the first version and every update.',
} as const;

type ReasonWithCopy = keyof typeof ESCALATION_REASON_COPY;

/**
 * The copy for a recorded reason, or null when there is none. An own-property
 * check rather than a bare index: the value comes off the wire as a string,
 * and a bare index on an object literal would return `Object.prototype`
 * members for keys like `constructor` or `toString`.
 */
function copyForRecordedReason(reason: string | null | undefined): string | null {
  if (!reason || !Object.prototype.hasOwnProperty.call(ESCALATION_REASON_COPY, reason)) {
    return null;
  }
  return ESCALATION_REASON_COPY[reason as ReasonWithCopy];
}

/**
 * Explains WHY a submission is sitting in human review, tying together the
 * risk score shown above the notice and the findings listed below it.
 *
 * **Prefer `escalationReason`** — the value the server actually recorded when
 * it escalated. This page used to re-derive the reason from the findings,
 * which meant the server's gate order was written out a second time here and
 * had to be kept in step by hand. It drifted twice: `incomplete_synthesis` has
 * no finding to key on and fell through to the risk-band copy (which is
 * false), and once the opaque-payload gate became conditional on corroborating
 * evidence, "an `encrypted_payload` finding exists" stopped meaning "that is
 * why this escalated" at all.
 *
 * The findings-derived chain below survives only as the fallback for reviews
 * predating the `escalation_reason` column (2026-08-13), where null is not a
 * claim that the reason was the score. Do not extend it — a new reason goes in
 * `ESCALATION_REASON_COPY`, keyed by the server's value.
 */
export function buildHumanReviewExplanation(input: {
  riskScore: number | null | undefined;
  findingsCount: number;
  requestsUserDocuments: boolean;
  hasUnreadableContent: boolean;
  hasUnreviewedSource: boolean;
  escalationReason?: string | null;
}): string {
  const recorded = copyForRecordedReason(input.escalationReason);
  if (recorded) return recorded;

  // Legacy rows only — see above.
  if (!input.escalationReason) {
    if (input.hasUnreviewedSource) {
      return ESCALATION_REASON_COPY.incomplete_review;
    }
    if (input.hasUnreadableContent) {
      return ESCALATION_REASON_COPY.encrypted_payload;
    }
    if (input.requestsUserDocuments) {
      return ESCALATION_REASON_COPY.sensitive_permission;
    }
  }
  const score =
    input.riskScore === null || input.riskScore === undefined ? null : `${input.riskScore} / 100`;
  if (input.findingsCount === 0) {
    return score
      ? `This submission's risk score of ${score} is above the range we publish automatically.`
      : 'This submission scored above the range we publish automatically.';
  }
  const findingWord = input.findingsCount === 1 ? 'finding' : 'findings';
  return score
    ? `The ${input.findingsCount} ${findingWord} below are what put this submission's risk score of ${score} above the range we publish automatically.`
    : `The ${input.findingsCount} ${findingWord} below put this submission above the range we publish automatically.`;
}

/**
 * Turns a failed approve/reject into something a reviewer can act on.
 *
 * `REVIEW_MUTATION_CONFLICT` is the interesting one: the claim was lost, which
 * in practice means another reviewer resolved this first. The honest
 * instruction is "reload", not "retry" — a retry would just lose the claim
 * again against the same newer version.
 *
 * Keyed on `ApiError.code`, not the message: the copy is the server's to
 * change, the code is the contract.
 */
export function describeDecisionFailure(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof ApiError && error.code === 'REVIEW_MUTATION_CONFLICT') {
    return 'Another reviewer resolved this submission first — no action was taken. Reloading the latest state.';
  }
  const message = error instanceof Error ? error.message : String(error);
  return `Could not record your decision: ${message}`;
}

/**
 * Why this submission no longer needs a decision, for the notice at the top of
 * the page. The reviewer's version also says what approving would do, because
 * that is the mistake the notice exists to prevent.
 */
export function describeSuperseded(input: {
  thisVersion: string | null;
  supersededBy: { status: string; manifestVersion: string | null; createdAt: string };
  canResolveReview: boolean;
}): string {
  const { supersededBy } = input;
  const newer = supersededBy.manifestVersion
    ? `Version ${supersededBy.manifestVersion}`
    : 'A newer version';
  const older = input.thisVersion ? `version ${input.thisVersion}` : 'this older version';
  const when = new Date(supersededBy.createdAt).toLocaleDateString();
  const state =
    supersededBy.status === 'published'
      ? `was submitted on ${when} and is published.`
      : `was submitted on ${when} and is in the publish pipeline.`;
  const consequence = input.canResolveReview
    ? supersededBy.status === 'published'
      ? ` Approving this review would build ${older} and then refuse to publish it, because an older version never replaces the live one. Close it as superseded instead.`
      : ` Approving this review would build ${older} while the newer one is still in flight. Close it as superseded instead.`
    : ' No action is needed on this one.';
  return `${newer} of this arche ${state}${consequence}`;
}

export function PluginSubmissionDetail() {
  const { submissionId } = useParams<{ submissionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = (user?.roles ?? []).includes('admin');
  // Support reaches this page from the review queue and should be sent back
  // there, but it does NOT get the approve/reject actions — those are keyed on
  // `canResolveReview` (mirrored by the server guard in routes/plugins/admin.ts).
  const isSupport = (user?.roles ?? []).includes('support');
  const isPluginApprover = (user?.roles ?? []).includes('plugin_approver');
  const canResolveReview = isAdmin || isPluginApprover;
  const cameFromReviewQueue = isAdmin || isSupport || isPluginApprover;
  // The two decision panels are mutually exclusive: opening one closes the
  // other, so a reviewer can never have an approve note and a reject reason
  // half-typed at once.
  const [openPanel, setOpenPanel] = useState<'approve' | 'reject' | null>(null);
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');
  const rejectModalOpen = openPanel === 'reject';
  const approvePanelOpen = openPanel === 'approve';
  // Closing a panel discards its draft, so a cancelled note cannot resurface
  // on the next open and be sent by accident.
  const closePanel = () => {
    setOpenPanel(null);
    setApproveNotes('');
    setRejectNotes('');
  };

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.submissions.detail(submissionId ?? '__none__'),
    queryFn: () => api.getPluginSubmission(submissionId!),
    enabled: !!submissionId,
  });

  // A refused decision refetches rather than only reporting: the usual reason
  // for a refusal is that another reviewer already resolved this review, and
  // the page is then showing actions that no longer apply.
  const onDecisionError = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.submissions.all });
  };

  const approveMutation = useMutation({
    // An empty note is sent as `undefined`, not '' — the server treats the
    // two the same, but only one of them reads as "no note" in the audit row.
    mutationFn: ({ reviewId, notes }: { reviewId: string; notes: string }) =>
      api.approvePluginReview(reviewId, notes || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions.all });
      closePanel();
      navigate('/dev-console/admin/reviews');
    },
    onError: onDecisionError,
  });

  const rejectMutation = useMutation({
    mutationFn: ({ reviewId, notes }: { reviewId: string; notes: string }) =>
      api.rejectPluginReview(reviewId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions.all });
      closePanel();
      navigate('/dev-console/admin/reviews');
    },
    onError: onDecisionError,
  });

  // Reviewers read the code they are deciding on, not just the analyzers'
  // summary of it. The URL is minted on click (it expires). It is an
  // attachment-disposition URL, so navigating to it saves the file and leaves
  // the page where it is — and unlike `window.open` after an await, that works
  // in Safari and under strict popup settings, where a popup not opened
  // synchronously from the click is blocked.
  const downloadSourceMutation = useMutation({
    mutationFn: (reviewId: string) => api.getPluginReviewSourceDownload(reviewId),
    onSuccess: ({ downloadUrl }) => {
      window.location.assign(downloadUrl);
    },
  });

  const closeSupersededMutation = useMutation({
    mutationFn: (reviewId: string) => api.closeSupersededPluginReview(reviewId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions.all });
      navigate('/dev-console/admin/reviews');
    },
    onError: onDecisionError,
  });

  if (isLoading) return <p className="p-8 text-muted-foreground">Loading...</p>;
  if (error)
    return <p className="p-8 text-destructive">Failed to load: {(error as Error).message}</p>;
  if (!data) return null;

  const { submission, review, supersededBy } = data;
  const archeName = (submission.manifest as { name?: string })?.name ?? submission.archId;
  const thisVersion = (submission.manifest as { version?: string })?.version ?? null;
  // Only a submission still waiting on something is "obsolete" in a way the
  // reader must act on; a rejected or published one is already settled.
  const isObsolete = !!supersededBy && submission.status !== 'rejected';

  const codeFindings = (review?.codeFindings ?? []) as FindingRow[];
  const dependencyFindings = (review?.dependencyFindings ?? []) as FindingRow[];
  const policyFindings = (review?.policyFindings ?? []) as FindingRow[];
  const qualityFindings = (review?.qualityFindings ?? []) as FindingRow[];

  const showAdminActions =
    canResolveReview && submission.status === 'human_review' && submission.reviewId;

  // The server refuses a decision whose claim was lost to a concurrent
  // reviewer. Left unreported, the button would just stop spinning and the
  // reviewer would reasonably assume it worked.
  const decisionError = describeDecisionFailure(
    approveMutation.error ?? rejectMutation.error ?? closeSupersededMutation.error ?? null,
  );
  const anyDecisionPending =
    approveMutation.isPending || rejectMutation.isPending || closeSupersededMutation.isPending;

  const manifestPermissions = ((submission.manifest as { permissions?: unknown })?.permissions ??
    []) as unknown[];
  // Permissions that reach data the plugin did not author (a user's documents,
  // signature requests it started) or write into the user's own files. Each
  // forces a human review, so each owes the contributor that rationale rather
  // than a score. The list mirrors the review gate's.
  const requestsUserDocuments = HUMAN_REVIEW_REQUIRED_PERMISSIONS.some((permission) =>
    manifestPermissions.includes(permission),
  );
  const humanReviewExplanation = buildHumanReviewExplanation({
    riskScore: review?.riskScore,
    findingsCount:
      codeFindings.length +
      dependencyFindings.length +
      policyFindings.length +
      qualityFindings.length,
    requestsUserDocuments,
    hasUnreadableContent: codeFindings.some((f) => f.category === 'encrypted_payload'),
    hasUnreviewedSource: codeFindings.some((f) => f.category === 'unreviewed_source'),
    escalationReason: review?.escalationReason,
  });

  return (
    <div className="max-w-5xl mx-auto p-8 space-y-6">
      <div>
        <Link
          to={cameFromReviewQueue ? '/dev-console/admin/reviews' : '/submissions'}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Link>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{archeName}</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono">{submission.submissionId}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Submitted {new Date(submission.createdAt).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {cameFromReviewQueue && submission.reviewId && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadSourceMutation.mutate(submission.reviewId!)}
              disabled={downloadSourceMutation.isPending}
            >
              {downloadSourceMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download submitted source
            </Button>
          )}
          <StatusBadge status={submission.status} />
        </div>
      </div>
      {downloadSourceMutation.error && (
        <p role="alert" className="text-sm text-destructive">
          Could not fetch the source archive: {(downloadSourceMutation.error as Error).message}
        </p>
      )}

      {isObsolete && supersededBy && (
        <Card
          role="status"
          className="p-4 bg-slate-100 dark:bg-slate-900/40 border-slate-300 dark:border-slate-700"
        >
          <p className="text-sm font-medium mb-1">
            {supersededBy.status === 'published'
              ? 'This submission is obsolete'
              : 'A newer submission has overtaken this one'}
          </p>
          <p className="text-sm text-muted-foreground">
            {describeSuperseded({ thisVersion, supersededBy, canResolveReview })}
          </p>
          <Link
            to={`/submissions/${supersededBy.submissionId}`}
            className="inline-flex items-center gap-1 mt-2 text-sm font-medium hover:underline"
          >
            Open the newer submission
            <ExternalLink className="h-3 w-3" />
          </Link>
        </Card>
      )}

      {review && (
        <Card className="p-4">
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">Risk score:</span>{' '}
              <span className="font-semibold">{review.riskScore ?? '—'} / 100</span>
            </div>
            <div>
              <span className="text-muted-foreground">Critical:</span>{' '}
              <span className="font-semibold">{review.criticalCount}</span>
            </div>
            <div>
              <span className="text-muted-foreground">High:</span>{' '}
              <span className="font-semibold">{review.highCount}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Files analyzed:</span>{' '}
              <span className="font-semibold">{review.filesAnalyzed}</span>
            </div>
          </div>
        </Card>
      )}

      {submission.status === 'human_review' && (
        <Card className="p-4 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900">
          <p className="text-sm font-medium mb-2">This is not a rejection</p>
          <p className="text-sm">{humanReviewExplanation}</p>
          <p className="text-sm mt-2">
            A human reviewer is taking a look — usually under 48 hours — and you'll get an email
            when it's decided.
            {!requestsUserDocuments && (
              <> You can wait, or fix the findings and resubmit; a new version replaces this one.</>
            )}
          </p>
        </Card>
      )}

      {submission.status === 'rejected' && submission.errorMessage && (
        <Card className="p-4 bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900">
          <p className="text-sm text-red-900 dark:text-red-300 font-medium mb-2">
            Submission rejected
          </p>
          <p className="text-sm">{submission.errorMessage}</p>
          {submission.archId && !isAdmin && (
            <Link
              to={`/a/${submission.archId}`}
              className="inline-flex items-center gap-1 mt-3 text-sm font-medium hover:underline"
            >
              Open plugin builder to fix
              <ExternalLink className="h-3 w-3" />
            </Link>
          )}
        </Card>
      )}

      {review?.status === 'approved' && review.humanNotes?.trim() && (
        <Card className="p-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
          <p className="text-sm font-medium mb-2">Note from the reviewer</p>
          <p className="text-sm whitespace-pre-wrap">{review.humanNotes}</p>
        </Card>
      )}

      {submission.status === 'published' && submission.archId && (
        <Card className="p-4 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900">
          <p className="text-sm font-medium mb-2">Your arche is live</p>
          <Link
            to={`/a/${submission.archId}`}
            className="inline-flex items-center gap-1 text-sm font-medium hover:underline"
          >
            Open arche
            <ExternalLink className="h-3 w-3" />
          </Link>
        </Card>
      )}

      <FindingsList title="Policy findings" findings={policyFindings} />
      <FindingsList title="Code findings" findings={codeFindings} />
      <FindingsList title="Dependency findings" findings={dependencyFindings} />
      <FindingsList title="Quality findings" findings={qualityFindings} />

      {showAdminActions && (
        <Card className="p-4 border-amber-300 dark:border-amber-700">
          <h3 className="font-semibold mb-3">Review actions</h3>
          {decisionError && (
            <p role="alert" className="mb-3 text-sm text-destructive">
              {decisionError}
            </p>
          )}
          <div className="flex gap-3 flex-wrap">
            {isObsolete && (
              <Button
                onClick={() => closeSupersededMutation.mutate(submission.reviewId!)}
                disabled={anyDecisionPending}
              >
                {closeSupersededMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Archive className="h-4 w-4 mr-2" />
                )}
                Close as superseded
              </Button>
            )}
            <Button
              // When a newer version exists, approving builds THIS older one only
              // for the release write to refuse it (the live version never goes
              // backwards) — keep the button, but not as the obvious choice.
              variant={isObsolete ? 'outline' : 'default'}
              onClick={() => setOpenPanel('approve')}
              disabled={anyDecisionPending}
            >
              <Check className="h-4 w-4 mr-2" />
              Approve & build
            </Button>
            <Button
              variant="destructive"
              onClick={() => setOpenPanel('reject')}
              disabled={anyDecisionPending}
            >
              <X className="h-4 w-4 mr-2" />
              Reject with notes
            </Button>
          </div>

          {approvePanelOpen && (
            <div className="mt-4 space-y-3">
              <Textarea
                placeholder="Optional note to the contributor — they will receive it by email and see it on this page."
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                maxLength={2000}
                rows={4}
              />
              <div className="flex gap-2">
                <Button
                  disabled={approveMutation.isPending}
                  onClick={() =>
                    approveMutation.mutate({
                      reviewId: submission.reviewId!,
                      notes: approveNotes.trim(),
                    })
                  }
                >
                  {approveMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  Confirm approve
                </Button>
                <Button variant="outline" onClick={closePanel}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {rejectModalOpen && (
            <div className="mt-4 space-y-3">
              <Textarea
                placeholder="Why are you rejecting this submission? The contributor will see this in the rejection email."
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                rows={4}
              />
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  disabled={!rejectNotes.trim() || rejectMutation.isPending}
                  onClick={() =>
                    rejectMutation.mutate({
                      reviewId: submission.reviewId!,
                      notes: rejectNotes.trim(),
                    })
                  }
                >
                  {rejectMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Confirm reject
                </Button>
                <Button variant="outline" onClick={closePanel}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
